import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, logout as logoutApi, isEmailVerificationRequiredError } from '../lib/api';
import { supabase, signOutSupabase, getSupabaseAccessToken } from '../lib/supabase';
import { logger } from '../utils/logger';
import { isPublicMarketingPath } from '../lib/marketingPaths';

const PENDING_VERIFY_KEY = 'shipdesi_pending_verify_email';

async function hasGuestNoSession() {
  if (typeof window !== 'undefined' && window.__SHIPDESI_E2E_MOCK_AUTH) return false;
  const token = await getSupabaseAccessToken();
  return !token;
}

/** Client idle timeout (ms). Override with REACT_APP_SESSION_IDLE_MS for tests.
 *  Playwright E2E can also set window.__SHIPDESI_SESSION_IDLE_MS before boot. */
function sessionIdleMs() {
  if (typeof window !== 'undefined' && window.__SHIPDESI_SESSION_IDLE_MS != null) {
    const n = Number(window.__SHIPDESI_SESSION_IDLE_MS);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const raw = process.env.REACT_APP_SESSION_IDLE_MS;
  if (raw != null && String(raw).trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const hours = Number(process.env.REACT_APP_SESSION_IDLE_HOURS);
  if (Number.isFinite(hours) && hours > 0) return hours * 3600 * 1000;
  // Align with backend SESSION_IDLE_HOURS default intent (4h) for UX.
  return 4 * 3600 * 1000;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(() => {
    try { return sessionStorage.getItem(PENDING_VERIFY_KEY) || null; } catch { return null; }
  });
  const navigate = useNavigate();
  const idleTimerRef = useRef(null);
  const logoutRef = useRef(null);

  const clearPendingVerification = useCallback(() => {
    setPendingVerificationEmail(null);
    try { sessionStorage.removeItem(PENDING_VERIFY_KEY); } catch { /* ignore */ }
  }, []);

  const storePendingVerificationEmail = useCallback((email) => {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) return;
    setPendingVerificationEmail(normalized);
    try { sessionStorage.setItem(PENDING_VERIFY_KEY, normalized); } catch { /* ignore */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      clearPendingVerification();
      if (res.data?.mfa_required) {
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (!path.startsWith('/login') && !path.startsWith('/auth/callback')) {
          navigate('/login?mfa=1', { replace: true });
        }
      }
    } catch (err) {
      setUser(null);
      // Log auth probe check errors (except routine 401s which indicate guest state)
      if (err.response?.status !== 401) {
        logger.warn('checkAuth failed', { error: err?.message || err });
      }
      if (err.response?.status === 401 && err.response?.data?.detail === 'mfa_required') {
        navigate('/login?mfa=1', { replace: true });
      } else if (isEmailVerificationRequiredError(err)) {
        const onLogin = typeof window !== 'undefined' && window.location.pathname.startsWith('/login');
        if (onLogin) {
          navigate('/login?verify=pending', { replace: true });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [clearPendingVerification, navigate]);

  const handleLogout = useCallback(async () => {
    try { await logoutApi(); } catch (err) {
      logger.warn('AuthContext backend logout call failed', { error: err?.message || err });
    }
    // signOutSupabase already logs the error and clears localStorage on failure.
    await signOutSupabase();
    setUser(null);
    clearPendingVerification();
    navigate('/', { replace: true, state: null });
  }, [clearPendingVerification, navigate]);

  logoutRef.current = handleLogout;

  useEffect(() => {
    // Phase 10: AuthCallback handles the Supabase OAuth code exchange.
    // Skip the /auth/me probe while we're on the callback page so we don't
    // race the supabase-js code-for-session exchange.
    // Phase 11 fix: same for /reset-password — recovery links establish a
    // Supabase session (often via SIGNED_IN) that must stay local to
    // ResetPassword until the user sets a new password or signs out.
    let cancelled = false;
    const path = window.location.pathname;
    if (path.startsWith('/auth/callback') || path.startsWith('/reset-password')) {
      setLoading(false);
      return undefined;
    }

    (async () => {
      // Anonymous visitors on marketing pages: skip guaranteed 401 /auth/me.
      if (isPublicMarketingPath(path) && await hasGuestNoSession()) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      if (!cancelled) checkAuth();
    })();

    if (!supabase) return () => { cancelled = true; };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Do not sync backend user while on the password-reset page.
      if (window.location.pathname.startsWith('/reset-password')) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        checkAuth();
      } else if (event === 'SIGNED_OUT') {
        // Playwright E2E injects a mock JWT without a real Supabase session;
        // gotrue may emit SIGNED_OUT when placeholder refresh fails — keep the
        // backend-authenticated user for that mode only.
        if (typeof window !== 'undefined' && window.__SHIPDESI_E2E_MOCK_AUTH) return;
        setUser(null);
      }
    });
    return () => {
      cancelled = true;
      try { subscription?.unsubscribe(); } catch { /* ignore */ }
    };
  }, [checkAuth]);

  // App-owned idle timeout: sign out after inactivity while a user session exists.
  useEffect(() => {
    if (!user) {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return undefined;
    }
    const idleMs = sessionIdleMs();
    const bump = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (logoutRef.current) logoutRef.current();
      }, idleMs);
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    bump();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      checkAuth,
      logout: handleLogout,
      pendingVerificationEmail,
      storePendingVerificationEmail,
      clearPendingVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
